import {
  PINYIN_FINALS,
  PINYIN_INITIALS,
  PINYIN_TONES,
  type PinyinFinal,
  type PinyinInitial,
  type PinyinTone,
  getPinyinFinal,
  getPinyinInitial,
  getPinyinTone,
} from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import {
  type GraphemeReadingPlacement,
  isGraphemeReadingIntroducedAtGrade,
} from "#grapheme-reading-introduction";
import {
  hardcodedPronunciationReadingAtSlot,
  orderIntroducedPinyinForGrapheme,
} from "#hardcoded-pronunciation-levels";
import type { GraphemeLessonPart, Level } from "#level";
import { formatPronunciationLevelLabel, getGraphemeLessonPart } from "#pronunciation-level";

/** A lesson that can introduce readings: a character lesson or a pronunciation-level slot. */
export type ReadingLesson = {
  /** The grade of the level that first lists the character, which bounds its secondary readings. */
  curriculumGrade: number | null;
  grapheme: string;
  /** The index of the lesson's character level, or of the one its pronunciation level follows. */
  levelIndex: number;
  /** The lesson's level as Browse numbers it after "Level ": `8`, or `1-P1` for a pronunciation level. */
  levelLabel: string;
  /** Whether the lesson sits on the character level itself or on the pronunciation level after it. */
  levelPart: "character" | "pronunciation";
  /** A pronunciation-level slot pinned to one reading (干 gan4, then 干 gan1 on 1-P1). */
  onlyPinyin?: string;
  part: GraphemeLessonPart;
};

/** One content-DB pronunciation card. */
export type GraphemeReadingCandidate = GraphemeReadingPlacement & {
  grapheme: string;
  pinyin: string;
};

/** The first reading the curriculum introduces with a given initial, final or tone. */
export type PinyinSoundIntroduction = {
  grapheme: string;
  /** Where Browse lists the lesson: see `ReadingLesson`. */
  levelIndex: number;
  levelLabel: string;
  levelPart: "character" | "pronunciation";
  /** Tone-number pinyin (`ben3`). */
  pinyin: string;
};

export type PinyinSoundIntroductions = {
  finals: ReadonlyMap<PinyinFinal, PinyinSoundIntroduction>;
  initials: ReadonlyMap<PinyinInitial, PinyinSoundIntroduction>;
  tones: ReadonlyMap<PinyinTone, PinyinSoundIntroduction>;
};

/** Graphemes per reading load: one `WHERE grapheme IN (…)` stays inside the web binding's limit. */
const READING_CHUNK_SIZE = 64;

/**
 * Every lesson that can introduce a reading, in the order the learner meets them: each level's
 * character lessons, then its pronunciation level (`buildUnitSequence`'s morpheme order).
 * Primitive and meaning-only lessons introduce no reading, so they are left out.
 */
export function listReadingLessons(levels: readonly Level[]): ReadingLesson[] {
  // As in enrollment, a character's readings are bounded by the grade of its first listing.
  const gradeByCharacter = new Map<string, number>();
  for (const level of levels) {
    for (const character of level.characters) {
      if (!gradeByCharacter.has(character)) {
        gradeByCharacter.set(character, level.curriculumGrade ?? 1);
      }
    }
  }

  const lessons: ReadingLesson[] = [];
  let pronunciationIndex = 0;
  for (const level of levels) {
    for (const grapheme of level.characters) {
      const part = getGraphemeLessonPart({ grapheme, isPronunciationLesson: false, level });
      if (part === "meaning" || part === "primitive") {
        continue;
      }
      lessons.push({
        curriculumGrade: gradeByCharacter.get(grapheme) ?? null,
        grapheme,
        levelIndex: level.index,
        levelLabel: String(level.index + 1),
        levelPart: "character",
        part,
      });
    }
    if (level.pronunciationCharacters === undefined) {
      continue;
    }
    const levelLabel = formatPronunciationLevelLabel(level, pronunciationIndex);
    pronunciationIndex += 1;
    for (const [slotIndex, grapheme] of level.pronunciationCharacters.entries()) {
      const hardcoded = hardcodedPronunciationReadingAtSlot({
        afterLevel: level.index + 1,
        grapheme,
        slotIndex,
      });
      lessons.push({
        curriculumGrade: gradeByCharacter.get(grapheme) ?? null,
        grapheme,
        levelIndex: level.index,
        levelLabel,
        levelPart: "pronunciation",
        ...(hardcoded === undefined ? {} : { onlyPinyin: hardcoded.pinyin }),
        part: "pronunciation",
      });
    }
  }
  return lessons;
}

/** The graphemes of the next lessons from `startIndex` whose readings are not loaded yet. */
function nextUnloadedGraphemes(
  lessons: readonly ReadingLesson[],
  startIndex: number,
  readingsByGrapheme: ReadonlyMap<string, unknown>,
): string[] {
  const graphemes = new Set<string>();
  for (
    let index = startIndex;
    index < lessons.length && graphemes.size < READING_CHUNK_SIZE;
    index += 1
  ) {
    const grapheme = lessons[index]?.grapheme;
    if (grapheme !== undefined && !readingsByGrapheme.has(grapheme)) {
      graphemes.add(grapheme);
    }
  }
  return [...graphemes];
}

/**
 * The first reading the curriculum introduces with each pinyin initial, final and tone, following
 * enrollment's rules: a lesson introduces the readings `isGraphemeReadingIntroducedAtGrade` admits,
 * and only for a grapheme that gets pronunciation cards at all (`hasPronunciationCards`).
 *
 * Readings are loaded a chunk of graphemes at a time (`readReadings`), in lesson order, and the walk
 * stops once every initial, final and tone has its reading: the Ready pack and the start of Starter
 * cover nearly all of them, so a few chunks usually suffice. A sound no lesson introduces is absent
 * from the result.
 */
export async function findPinyinSoundIntroductions({
  hasPronunciationCards,
  lessons,
  readReadings,
  script,
}: {
  hasPronunciationCards: (grapheme: string) => boolean;
  lessons: readonly ReadingLesson[];
  readReadings: (graphemes: readonly string[]) => Promise<readonly GraphemeReadingCandidate[]>;
  script: "simplified" | "traditional";
}): Promise<PinyinSoundIntroductions> {
  const initials = new Map<PinyinInitial, PinyinSoundIntroduction>();
  const finals = new Map<PinyinFinal, PinyinSoundIntroduction>();
  const tones = new Map<PinyinTone, PinyinSoundIntroduction>();
  const readingsByGrapheme = new Map<string, GraphemeReadingCandidate[]>();
  for (const [lessonIndex, lesson] of lessons.entries()) {
    if (
      initials.size === PINYIN_INITIALS.length &&
      finals.size === PINYIN_FINALS.length &&
      tones.size === PINYIN_TONES.length
    ) {
      break;
    }
    if (!readingsByGrapheme.has(lesson.grapheme)) {
      const chunk = nextUnloadedGraphemes(lessons, lessonIndex, readingsByGrapheme);
      for (const grapheme of chunk) {
        readingsByGrapheme.set(grapheme, []);
      }
      // Sequential on purpose: each chunk is read only if the previous ones fell short.
      // oxlint-disable-next-line no-await-in-loop -- see above
      for (const reading of await readReadings(chunk)) {
        readingsByGrapheme.get(reading.grapheme)?.push(reading);
      }
    }
    if (!hasPronunciationCards(lesson.grapheme)) {
      continue;
    }
    const introducedPinyin = (readingsByGrapheme.get(lesson.grapheme) ?? [])
      .filter((reading) =>
        isGraphemeReadingIntroducedAtGrade({
          curriculumGrade: lesson.curriculumGrade,
          grapheme: lesson.grapheme,
          onlyPinyin: lesson.onlyPinyin,
          part: lesson.part,
          pinyin: reading.pinyin,
          reading,
          script,
        }),
      )
      .map(({ pinyin }) => pinyin);
    for (const pinyin of orderIntroducedPinyinForGrapheme(lesson.grapheme, introducedPinyin)) {
      const introduction = {
        grapheme: lesson.grapheme,
        levelIndex: lesson.levelIndex,
        levelLabel: lesson.levelLabel,
        levelPart: lesson.levelPart,
        pinyin,
      };
      const initial = getPinyinInitial(pinyin);
      if (initial !== null && !initials.has(initial)) {
        initials.set(initial, introduction);
      }
      const final = getPinyinFinal(pinyin);
      if (final !== null && !finals.has(final)) {
        finals.set(final, introduction);
      }
      const tone = getPinyinTone(pinyin);
      if (tone !== null && !tones.has(tone)) {
        tones.set(tone, introduction);
      }
    }
  }
  return { finals, initials, tones };
}
